// src/views/student/StudentGroupModal/StudentGroupModal.tsx
import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Button, Checkbox, Input, Modal,
} from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classes from './StudentGroupModal.module.less';
import AppModal, { ModalButtons } from '../../../components/AppModal';
import API from '../../../services/ApiService';
import { StudentGroup, UserData } from '../../../services/ApiService.types';
import { success, error } from '../../../components/Message';
import {
  fetchStudentGroups, putGroup, refreshStudentsPage, removeGroup,
} from '../slice/actions';
import { matchesSearch, toSearchWords } from '../../../utils/search';
import { getApiErrorCode, getApiErrorMessage } from '../../../utils/apiErrors';

type StudentGroupModalProps = {
  // `null` — nowa grupa
  group: StudentGroup | null;
  // Wszyscy uczniowie nauczyciela, nie tylko widoczni na ekranie
  students: UserData[];
  hideModal: () => void;
  // Po zapisaniu ekran pokazuje zapisaną grupę
  onSaved: (group: StudentGroup) => void;
  onDeleted: () => void;
};

// Okno „Nowa grupa” / „Edytuj grupę”: nazwa i zaznaczanie uczniów, w edycji także „Usuń grupę”.
// Zapis wysyła dokładny skład grupy. Ani wypisanie z grupy, ani jej usunięcie nikomu list nie zabiera
const StudentGroupModal: FC<StudentGroupModalProps> = ({
  group, students, hideModal, onSaved, onDeleted,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [name, setName] = useState(group?.name ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>(group?.studentIds ?? []);
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Po 404 `STUDENT_NOT_FOUND` lista uczniów się odświeża. Dopiero gdy przyjdzie, odznaczamy tych, których już
  // nie ma — wcześniej (np. przy jeszcze niewczytanej liście) odznaczylibyśmy członków grupy
  const pruneMissingRef = useRef(false);

  useEffect(() => {
    if (!pruneMissingRef.current) {
      return;
    }

    pruneMissingRef.current = false;
    const existingIds = new Set(students.map((student) => student.id));
    setSelectedIds((prev) => prev.filter((id) => existingIds.has(id)));
  }, [students]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Szukanie jak na ekranie z listami — w imieniu, nazwisku i adresie, słowa w dowolnej kolejności
  const visibleStudents = useMemo(() => {
    const searchWords = toSearchWords(search);

    return students.filter((student) => (
      matchesSearch(`${student.name} ${student.surname} ${student.email}`, searchWords)
    ));
  }, [students, search]);

  const toggleStudent = useCallback((studentId: number) => {
    setSelectedIds((prev) => (prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]));
  }, []);

  const onNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError(null);
  }, []);

  const save = useCallback(async () => {
    const trimmedName = name.trim();

    // Górny limit (100 znaków) pilnuje samo pole
    if (!trimmedName) {
      setNameError(t('student:groups.messages.nameRequired'));

      return;
    }

    setIsSaving(true);

    try {
      const data = { name: trimmedName, studentIds: selectedIds };
      const { data: savedGroup } = group
        ? await API.studentGroups.update(group.id, data)
        : await API.studentGroups.create(data);

      dispatch(putGroup(savedGroup));
      success(t(group ? 'student:groups.messages.saved' : 'student:groups.messages.added', { group: savedGroup.name }));
      // Okno się zamyka, więc jego stanu już nie zmieniamy
      onSaved(savedGroup);
    } catch (e) {
      setIsSaving(false);

      const code = getApiErrorCode(e);

      if (code === 'GROUP_NAME_TAKEN') {
        setNameError(getApiErrorMessage(e, t('student:groups.messages.nameTaken')));
      } else if (code === 'GROUP_NOT_FOUND') {
        // Grupę usunięto w międzyczasie, np. w drugiej karcie
        error(getApiErrorMessage(e, t('student:groups.messages.gone')));
        dispatch(fetchStudentGroups());
        hideModal();
      } else if (code === 'STUDENT_NOT_FOUND') {
        // Własny tekst zamiast serwerowego „Odśwież stronę…”: lista odświeża się tu sama i odznacza brakujących,
        // a odświeżenie strony skasowałoby to, co nauczyciel wpisał w oknie
        error(t('student:groups.messages.studentGone'));
        pruneMissingRef.current = true;
        dispatch(refreshStudentsPage());
        dispatch(fetchStudentGroups());
      } else {
        error(t('student:groups.messages.saveError'));
      }
    }
  }, [dispatch, group, hideModal, name, onSaved, selectedIds, t]);

  const confirmDelete = useCallback(() => {
    if (!group) {
      return;
    }

    Modal.confirm({
      title: t('student:groups.delete'),
      content: t('student:groups.deleteConfirm', { group: group.name }),
      okText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await API.studentGroups.delete(group.id);
        } catch (e) {
          // Grupy już nie ma, np. usunięta w drugiej karcie — skutek ten sam, co udanego usunięcia
          if (getApiErrorCode(e) !== 'GROUP_NOT_FOUND') {
            error(t('student:groups.messages.deleteError'));

            return;
          }
        }

        dispatch(removeGroup(group.id));
        success(t('student:groups.messages.deleted', { group: group.name }));
        onDeleted();
      },
    });
  }, [dispatch, group, onDeleted, t]);

  return (
    <AppModal title={group ? t('student:groups.edit') : t('student:groups.new')} onCancel={hideModal} wide>
      <div className={classes.content}>
        <div className={classes.label}>{t('student:groups.name')}</div>
        <Input
          aria-label={t('student:groups.name')}
          className={classes.nameInput}
          value={name}
          maxLength={100}
          onChange={onNameChange}
          onPressEnter={save}
          autoFocus
        />
        {nameError && <p className={classes.error}>{nameError}</p>}

        <div className={classes.label}>
          <span>{t('student:groups.students')}</span>
          <span className={classes.counter}>{t('student:groups.selected', { n: selectedIds.length })}</span>
        </div>
        <Input
          aria-label={t('student:list.search')}
          className={classes.search}
          placeholder={t('student:list.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <div className={classes.studentsBox}>
          {visibleStudents.length > 0 ? (
            visibleStudents.map((student) => (
              <Checkbox
                key={student.id}
                className={classes.studentRow}
                checked={selectedSet.has(student.id)}
                onChange={() => toggleStudent(student.id)}
              >
                <span className={classes.studentName}>{student.name} {student.surname}</span>
              </Checkbox>
            ))
          ) : (
            <p className={classes.empty}>
              {students.length > 0 ? t('student:list.empty.noMatch') : t('student:groups.noStudents')}
            </p>
          )}
        </div>

        <ModalButtons>
          {group && (
            <Button type="primary" danger onClick={confirmDelete} disabled={isSaving}>
              {t('student:groups.delete')}
            </Button>
          )}
          <Button onClick={hideModal}>
            {t('common:buttons.cancel')}
          </Button>
          <Button type="primary" onClick={save} loading={isSaving}>
            {t('common:buttons.save')}
          </Button>
        </ModalButtons>
      </div>
    </AppModal>
  );
};

export default StudentGroupModal;
